// 5 · Front-desk checkout (Checkout.dc.html; 1366×768, responsive).
// A table is paid per CHECK (docs/DINING_API_2026-10-28.md §12). A check is
// shown paid only when the API says so: a card start answers "waiting", and
// only Aster Gateway's verdict (read by polling the payment) books it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PaymentStatusPanel from '@/components/payments/PaymentStatusPanel';
import { checkoutPanelFetcher, thisTerminal, usePaymentStatusPanel } from '@/components/payments/usePaymentStatusPanel';
import { getAuthToken } from '@/lib/authToken';
import { useUpdateHold } from '@/pwa/autoUpdate';
import { api, errorText, newKey } from '../lib/api';
import { clock, clockSeconds, elapsed, modifierText, money, plural, shareLabel, shortName } from '../lib/format';
import { useLive } from '../lib/useLive';
import { ROLE_LABEL, useDiningSession } from '../lib/session';
import { C, FONT_TIGHT, TABLE_STATE } from '../theme/tokens';
import { useApproval } from '../components/ApprovalDialog';
import { PartCardModal, ReceiptActions, RefundModal } from '../components/CheckoutExtras';
import SplitPayFlow, { checkView } from '../components/SplitPayFlow';
import { UserMenu } from '../components/Shell';
import {
  Btn, Chip, Clock, Empty, ErrorNote, FullError, inputStyle, LiveIndicator, Modal, ModalFoot, ModalHead, Segmented,
  Skeleton, StreamBanner, useNow, useToast,
} from '../components/ui';
import {
  IconArrowLeft, IconArrowRight, IconBackspace, IconCard, IconCheck, IconClose, IconContactless, IconShield, IconSplit,
  IconTerminal,
} from '../components/icons';

const REGISTER_KEY = 'dining_register_terminal_id';
const POLL_MS = 1500;
const CHECK_TONES = [
  { ring: '#FDBA74', bg: '#FFF7F0', border: '#FED7AA', fg: '#9A3412' },
  { ring: '#93C5FD', bg: '#EFF6FF', border: '#BFDBFE', fg: '#1D4ED8' },
  { ring: '#86EFAC', bg: '#F0FDF4', border: '#BBF7D0', fg: '#15803D' },
  { ring: '#C4B5FD', bg: '#F5F3FF', border: '#DDD6FE', fg: '#6D28D9' },
];
const tone = (i) => CHECK_TONES[i % CHECK_TONES.length];
const MANUAL = [
  { value: 'gift_card', label: 'Gift card' }, { value: 'zelle', label: 'Zelle' }, { value: 'venmo', label: 'Venmo' },
  { value: 'loyalty', label: 'Loyalty' }, { value: 'crypto', label: 'Crypto' },
];
const METHOD_LABEL = { cash: 'Cash', gift_card: 'Gift card', zelle: 'Zelle', venmo: 'Venmo', loyalty: 'Loyalty', crypto: 'Crypto' };

function readRegister() {
  try { return Number(localStorage.getItem(REGISTER_KEY)) || null; } catch (e) { return null; }
}

// ------------------------------------------------------------------ checkout home (which table)

export function LightHeader({ me, title, sub, chip, right }) {
  return (
    <header style={{ height: 64, flex: 'none', display: 'flex', alignItems: 'center', gap: 16, padding: '0 20px', background: '#FFFFFF', borderBottom: '1px solid #ECE6DF', minWidth: 0 }}>
      <div aria-hidden="true" className="c29-tight" style={{ width: 38, height: 38, borderRadius: 10, background: C.orange, color: C.orangeDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, flex: 'none' }}>29</div>
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, flex: 'none' }}><span style={{ fontSize: 17, fontWeight: 800 }}>{title}</span><span style={{ fontSize: 12, color: C.muted }}>{sub}</span></div>
      {chip && <span style={{ height: 36, padding: '0 12px', borderRadius: 10, background: C.sand, fontSize: 14, fontWeight: 600, color: C.ink2, display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{chip}</span>}
      <div style={{ flex: 1 }} />
      {right}
      <Clock size={20} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
        <UserMenu me={me} dark={false} size={38} />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}><span style={{ fontSize: 13, fontWeight: 700 }}>{shortName(me?.name)}</span><span style={{ fontSize: 11.5, color: C.muted }}>{ROLE_LABEL[me?.role] || me?.role}</span></div>
      </div>
    </header>
  );
}

export function CheckoutHome() {
  const { me } = useDiningSession();
  const navigate = useNavigate();
  const floor = useLive(() => api.get('/dining/floor'), []);
  const tables = (floor.data?.tables || []).filter((t) => t.session);
  const order = { checkout_requested: 0, needs_attention: 1, food_ready: 2, dining: 3, ordering: 4, seated: 5 };
  tables.sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9) || (b.session.seated_minutes || 0) - (a.session.seated_minutes || 0));
  return (
    <div className="c29 c29-light" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <LightHeader me={me} title="Checkout" sub="Front desk" right={(
        <>
          <button type="button" onClick={() => navigate('/dining/checkout/closed')} className="c29-press" data-testid="closed-checks-link"
            style={{ height: 44, padding: '0 14px', borderRadius: 12, border: `1px solid ${C.border}`, background: '#FFFFFF', color: C.ink2, fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>Closed checks</button>
          <LiveIndicator status={floor.streamStatus} light />
        </>
      )} />
      <StreamBanner status={floor.streamStatus} light onRetry={floor.reload} />
      <div className="c29-scroll" style={{ flex: 1, padding: 20 }}>
        {floor.loading && !floor.data && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={128} style={{ borderRadius: 18 }} />)}</div>}
        {floor.error && !floor.data && <FullError light error={floor.error} onRetry={floor.reload} />}
        {floor.data && !tables.length && <Empty light icon={<IconCard size={28} />} title="No open tables" body="Tables with guests show here. A table that asks for the check moves to the top." />}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          {tables.map((t) => {
            const st = TABLE_STATE[t.status] || TABLE_STATE.dining;
            const asked = t.status === 'checkout_requested';
            return (
              <button key={t.id} type="button" className="c29-press" onClick={() => navigate(`/dining/checkout/${t.session.id}`)}
                style={{ textAlign: 'left', background: '#FFFFFF', borderRadius: 18, border: asked ? `2px solid ${C.violetHi}` : `1px solid ${C.border}`, padding: 16, display: 'flex', flexDirection: 'column', gap: 10, color: C.ink, minHeight: 128 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontFamily: FONT_TIGHT, fontSize: 24, fontWeight: 800 }}>Table {t.table_number}</span>
                  <Chip bg={asked ? C.violetSoft : C.sand} fg={asked ? C.violet : C.ink3} dot={st.ring}>{st.label}</Chip>
                </div>
                <span style={{ fontSize: 13, color: C.muted }}>{plural(t.session.party_size, 'guest')}{t.session.server_name ? ` · ${shortName(t.session.server_name)}` : ''} · {t.session.seated_minutes ?? 0} min</span>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 'auto' }}>
                  <span className="c29-caps" style={{ color: C.faint }}>Balance</span>
                  <span style={{ fontFamily: FONT_TIGHT, fontSize: 26, fontWeight: 800 }}>{t.balance_error ? '—' : money(t.open_balance_cents ?? 0)}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ pieces

function TotalsRows({ src }) {
  if (!src) return null;
  if (src.error) return <ErrorNote light compact error={typeof src.error === 'string' ? src.error : 'This check cannot be totalled yet.'} />;
  const row = (label, value, color) => <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{label}</span><span style={{ color }}>{value}</span></div>;
  return (
    <>
      {row('Subtotal', money(src.subtotal_cents))}
      {src.discount_cents > 0 && row('Discount', `−${money(src.discount_cents)}`, C.greenInk)}
      {src.comp_cents > 0 && row('Comp', `−${money(src.comp_cents)}`, C.greenInk)}
      {row(`Tax${src.tax_rate_percent != null ? ` ${Number(src.tax_rate_percent)}%` : ''}`, money(src.tax_cents))}
      {row('Service charge', src.service_charge_cents > 0 ? money(src.service_charge_cents) : 'None')}
      {row('Tip', src.tip_cents > 0 ? money(src.tip_cents) : 'None yet')}
      {src.paid_cents > 0 && row('Paid', money(src.paid_cents), C.greenInk)}
      {src.refunded_cents > 0 && row('Refunded', money(src.refunded_cents), C.redInk)}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', color: C.ink, marginTop: 2 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Check total</span>
        <span style={{ fontFamily: FONT_TIGHT, fontSize: 22, fontWeight: 800 }}>{money(src.total_cents)}</span>
      </div>
    </>
  );
}

function Keypad({ value, onChange, quick }) {
  const press = (k) => {
    if (k === 'del') onChange(value.slice(0, -1));
    else onChange((value + k).replace(/^0+/, '').slice(0, 7));
  };
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${quick.length}, minmax(0, 1fr))`, gap: 8 }}>
        {quick.map((q) => (
          <button key={q.label} type="button" className="c29-press" onClick={() => onChange(String(q.cents))}
            style={{ height: 50, borderRadius: 14, border: `1px solid ${C.border}`, background: '#FFFFFF', color: C.ink, fontSize: 16, fontWeight: 700 }}>{q.label}</button>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 200, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gridAutoRows: '1fr', gap: 8 }}>
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '00', '0', 'del'].map((k) => (
          <button key={k} type="button" className="c29-press c29-tight" onClick={() => press(k)} aria-label={k === 'del' ? 'Delete' : k}
            style={{ borderRadius: 14, border: 'none', background: '#FFFFFF', color: C.ink, fontSize: 26, fontWeight: 700, boxShadow: '0 1px 0 #E7E0D8', minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {k === 'del' ? <IconBackspace size={24} /> : k}
          </button>
        ))}
      </div>
    </>
  );
}

function Step({ state, label, time }) {
  const icon = state === 'done'
    ? <span style={{ width: 22, height: 22, borderRadius: '50%', background: C.green, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><IconCheck size={13} sw={3.4} /></span>
    : state === 'active'
      ? <span style={{ width: 22, height: 22, borderRadius: '50%', border: `2.5px solid ${C.orange}`, flex: 'none' }} />
      : <span style={{ width: 22, height: 22, borderRadius: '50%', border: '2px dashed #D6D3D1', flex: 'none' }} />;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: state === 'active' ? 700 : 500, color: state === 'todo' ? C.faint : C.ink }}>
      {icon}<span style={{ flex: 1 }}>{label}</span>{time && <span style={{ color: state === 'active' ? C.orangeInk : C.faint }}>{time}</span>}
    </div>
  );
}

// ------------------------------------------------------------------ split / move / adjust / other

function SplitModal({ open, onClose, items, partySize, total, onCreate, busy, error }) {
  const [tab, setTab] = useState('even');
  const [ways, setWays] = useState(2);
  const [groups, setGroups] = useState([[], []]);
  useEffect(() => {
    if (!open) return;
    setTab('even'); setWays(Math.max(2, Math.min(partySize || 2, 4)));
    setGroups([items.map((i) => i.id), []]);
  }, [open, items, partySize]);
  if (!open) return null;
  const seats = {};
  items.forEach((i) => { const k = i.seat_number || 0; (seats[k] = seats[k] || []).push(i); });
  const seatKeys = Object.keys(seats).map(Number).filter((k) => k > 0).sort((a, b) => a - b);
  const share = Math.floor(total / ways);
  const extra = total - share * ways;
  const byId = Object.fromEntries(items.map((i) => [i.id, i]));
  const moveTo = (id, to) => setGroups((gs) => {
    const next = gs.map((g) => g.filter((x) => x !== id));
    while (next.length <= to) next.push([]);
    next[to] = [...next[to], id];
    return next;
  });
  const nonEmpty = groups.filter((g) => g.length);
  const create = () => {
    if (tab === 'even') onCreate({ mode: 'even', ways });
    else if (tab === 'seat') onCreate({ mode: 'by_seat' });
    else if (tab === 'item') onCreate({ mode: 'by_item', groups: nonEmpty });
    else onCreate({ mode: 'single' });
  };
  const label = tab === 'even' ? `Create ${ways} checks` : tab === 'seat' ? `Create ${seatKeys.length} checks` : tab === 'item' ? `Create ${nonEmpty.length} checks` : 'One check';
  return (
    <Modal open label="Split check" onClose={onClose} width={860}>
      <ModalHead title="Split check" sub={`${plural(partySize || 1, 'guest')} · ${money(total)} incl. tax`} onClose={onClose}>
        <Segmented value={tab} onChange={setTab} style={{ flex: 'none' }} options={[
          { value: 'even', label: 'Split evenly' }, { value: 'seat', label: 'By seat', disabled: !seatKeys.length }, { value: 'item', label: 'By item' }, { value: 'single', label: 'One check' },
        ]} />
      </ModalHead>
      <div className="c29-scroll" style={{ padding: '20px 24px', minHeight: 330, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {tab === 'even' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 700, width: 150 }}>How many ways?</span>
              {[2, 3, 4, 5, 6].map((w) => (
                <button key={w} type="button" aria-pressed={w === ways} onClick={() => setWays(w)} className="c29-press c29-tight"
                  style={{ width: 72, height: 60, borderRadius: 14, border: w === ways ? `2px solid ${C.orange}` : `1px solid ${C.border}`, background: w === ways ? C.orangeTint : '#FFFFFF', color: w === ways ? '#9A3412' : C.ink, fontSize: 24, fontWeight: 800 }}>{w}</button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(ways, 6)}, minmax(0, 1fr))`, gap: 12 }}>
              {Array.from({ length: ways }, (_, i) => (
                <div key={i} style={{ borderRadius: 16, background: '#FAF8F5', border: '1px solid #ECE6DF', padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>Check {i + 1}</span>
                  <span style={{ fontFamily: FONT_TIGHT, fontSize: ways > 4 ? 26 : 34, fontWeight: 800, letterSpacing: '-1px' }}>≈{money(share + (i < extra ? 1 : 0))}</span>
                  <span style={{ fontSize: 12.5, color: C.faint }}>1/{ways} of every item</span>
                </div>
              ))}
            </div>
            <span style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>Each check gets an equal share of every item; tax is worked out per check, so the amounts can differ by a cent. Guests add their own tip on the terminal.</span>
          </>
        )}
        {tab === 'seat' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
              {seatKeys.map((k, i) => (
                <div key={k} style={{ borderRadius: 16, border: `2px solid ${tone(i).ring}`, background: '#FFFFFF', padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: 15, fontWeight: 800 }}>Seat {k}</span><Chip bg={tone(i).bg} fg={tone(i).fg} height={22}>Check {i + 1}</Chip></div>
                  {seats[k].map((it) => <span key={it.id} style={{ fontSize: 13, color: C.ink3 }}>{it.quantity > 1 ? `${it.quantity}× ` : ''}{it.name}</span>)}
                  <div style={{ flex: 1 }} />
                  <span style={{ fontFamily: FONT_TIGHT, fontSize: 20, fontWeight: 800 }}>{money(seats[k].reduce((n, it) => n + it.total_cents, 0))}<span style={{ fontSize: 12, color: C.faint, fontWeight: 600, fontFamily: 'Inter' }}> + tax</span></span>
                </div>
              ))}
            </div>
            {seats[0] && <span style={{ fontSize: 13, color: C.muted }}>{plural(seats[0].length, 'item')} with no seat ({seats[0].map((i) => i.name).join(', ')}) {seats[0].length === 1 ? 'is' : 'are'} shared evenly over the seat checks.</span>}
          </>
        )}
        {tab === 'item' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(groups.length + 1, 4)}, minmax(0, 1fr))`, gap: 16 }}>
              {groups.map((g, gi) => (
                <div key={gi} style={{ borderRadius: 16, background: tone(gi).bg, border: `1.5px solid ${tone(gi).border}`, padding: 14, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 220 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 15, fontWeight: 800, color: tone(gi).fg }}>Check {gi + 1}</span><span style={{ fontSize: 15, fontWeight: 800 }}>{money(g.reduce((n, id) => n + (byId[id]?.total_cents || 0), 0))}</span></div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {g.map((id) => (
                      <button key={id} type="button" className="c29-press" onClick={() => moveTo(id, (gi + 1) % Math.max(groups.length, 2))}
                        title="Tap to move to the next check"
                        style={{ minHeight: 40, padding: '0 12px', borderRadius: 10, background: '#FFFFFF', border: `1px solid ${tone(gi).border}`, fontSize: 13.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8, color: C.ink }}>
                        {byId[id]?.seat_number ? <span style={{ fontSize: 11, color: C.faint }}>S{byId[id].seat_number}</span> : null}{byId[id]?.name} <b>{money(byId[id]?.total_cents)}</b>
                      </button>
                    ))}
                    {!g.length && <span style={{ fontSize: 13, color: C.faint }}>Tap items in another check to move them here.</span>}
                  </div>
                </div>
              ))}
              {groups.length < 4 && (
                <button type="button" onClick={() => setGroups((gs) => [...gs, []])}
                  style={{ borderRadius: 16, border: `2px dashed ${C.blueHi}`, background: 'rgba(59,130,246,.06)', color: C.blueInk, fontSize: 14, fontWeight: 700, minHeight: 220 }}>+ Add a check</button>
              )}
            </div>
            <span style={{ fontSize: 13, color: C.muted }}>Tap an item to move it to the next check. Every item goes on exactly one check; tax follows the items.</span>
          </>
        )}
        {tab === 'single' && <span style={{ fontSize: 15, color: C.ink3, lineHeight: 1.5 }}>One check with every item - pay it with one card, or with several tenders.</span>}
        {error && <ErrorNote light error={error} compact />}
      </div>
      <ModalFoot>
        <Btn kind="white" height={56} onClick={onClose}>Cancel</Btn>
        <Btn kind="orange" height={56} busy={busy} disabled={tab === 'item' && nonEmpty.length < 1} onClick={create} style={{ padding: '0 28px' }}>{label}</Btn>
      </ModalFoot>
    </Modal>
  );
}

function AdjustModal({ open, kind, onClose, onApply, subtotal, needsCode }) {
  const [mode, setMode] = useState('percent');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setMode('percent'); setValue(''); setReason(''); setError(null); } }, [open, kind]);
  if (!open) return null;
  const title = kind === 'discount' ? 'Discount' : kind === 'comp' ? 'Comp' : 'Service charge';
  const presets = kind === 'service_charge' ? [18, 20, 22] : kind === 'comp' ? [50, 100] : [10, 15, 20];
  const num = Number(value);
  const amount = mode === 'percent' ? Math.round((subtotal || 0) * (num || 0) / 100) : Math.round((num || 0) * 100);
  const needsReason = kind !== 'service_charge';
  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { kind, reason: reason.trim() || null };
      if (mode === 'percent') body.percent = num; else body.amount_cents = Math.round(num * 100);
      await onApply(body);
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open label={title} onClose={onClose} width={520}>
      <ModalHead title={title} sub={`${kind === 'service_charge' ? 'Not taxed' : 'Before tax'}${needsCode ? ' · an owner’s or manager’s approval code is needed' : ''}`} onClose={onClose} />
      <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Segmented value={mode} onChange={(m) => { setMode(m); setValue(''); }} options={[{ value: 'percent', label: 'Percent' }, { value: 'amount', label: 'Amount' }]} />
        {mode === 'percent' && (
          <div style={{ display: 'flex', gap: 8 }}>
            {presets.map((p) => (
              <button key={p} type="button" aria-pressed={num === p} onClick={() => setValue(String(p))} className="c29-press"
                style={{ flex: 1, height: 52, borderRadius: 12, border: num === p ? `2px solid ${C.orange}` : `1px solid ${C.border}`, background: num === p ? C.orangeTint : '#FFFFFF', color: num === p ? '#9A3412' : C.ink, fontSize: 17, fontWeight: 800 }}>{p}%</button>
            ))}
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{mode === 'percent' ? 'Percent' : 'Amount ($)'}</span>
          <input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value.replace(/[^0-9.]/g, '').slice(0, 8))} style={inputStyle} />
          {num > 0 && <span style={{ fontSize: 12.5, color: C.muted }}>≈ {money(amount)}{mode === 'percent' ? ` of ${money(subtotal)}` : ''}</span>}
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Reason{needsReason ? '' : ' (optional)'}</span>
          <input value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder={kind === 'comp' ? 'e.g. remade - long wait' : kind === 'discount' ? 'e.g. birthday' : 'e.g. party of 8'} style={inputStyle} />
        </label>
        {error && <ErrorNote light error={error} compact />}
      </div>
      <ModalFoot>
        <Btn kind="white" height={56} onClick={onClose}>Cancel</Btn>
        <Btn kind="ink" height={56} busy={busy} disabled={!(num > 0) || (needsReason && !reason.trim())} onClick={apply}>Apply {title.toLowerCase()}</Btn>
      </ModalFoot>
    </Modal>
  );
}

function OtherModal({ open, onClose, due, onPay }) {
  const [method, setMethod] = useState('gift_card');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setMethod('gift_card'); setReference(''); setAmount(''); setError(null); } }, [open]);
  if (!open) return null;
  const cents = amount ? Math.round(Number(amount) * 100) : due;
  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      await onPay({ method, reference: reference.trim() || null, amount_cents: cents < due ? cents : undefined });
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open label="Other payment" onClose={onClose} width={560}>
      <ModalHead title="Other payment" sub={`Balance due ${money(due)}`} onClose={onClose} />
      <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
          {MANUAL.map((m) => (
            <button key={m.value} type="button" aria-pressed={method === m.value} onClick={() => setMethod(m.value)} className="c29-press"
              style={{ height: 52, borderRadius: 12, border: method === m.value ? `2px solid ${C.ink}` : `1px solid ${C.border}`, background: method === m.value ? C.ink : '#FFFFFF', color: method === m.value ? '#FFFFFF' : C.ink, fontSize: 15, fontWeight: 700 }}>{m.label}</button>
          ))}
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><span style={{ fontSize: 14, fontWeight: 700 }}>Reference</span><input value={reference} maxLength={100} onChange={(e) => setReference(e.target.value)} placeholder="Card number, confirmation code…" style={inputStyle} /></label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><span style={{ fontSize: 14, fontWeight: 700 }}>Amount ($) · empty = the whole balance</span><input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, '').slice(0, 8))} placeholder={(due / 100).toFixed(2)} style={inputStyle} /></label>
        <span style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>Recorded as taken by you. Confirm the payment went through before you save it.</span>
        {error && <ErrorNote light error={error} compact />}
      </div>
      <ModalFoot>
        <Btn kind="white" height={56} onClick={onClose}>Cancel</Btn>
        <Btn kind="ink" height={56} busy={busy} disabled={!(cents > 0) || cents > due} onClick={pay}>Record {money(Math.min(cents, due))}</Btn>
      </ModalFoot>
    </Modal>
  );
}

function MoveModal({ line, checks, fromCheck, onClose, onMove }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!line) return null;
  const [num, den] = String(line.share || '1/1').split('/').map(Number);
  const targets = checks.filter((c) => c.status === 'open' && c.id !== fromCheck.id);
  const move = async (to, half) => {
    setBusy(true);
    setError(null);
    try {
      await onMove({ order_item_id: line.order_item_id, from_check_id: fromCheck.id, to_check_id: to, share_num: num, share_den: half ? den * 2 : den });
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open label="Move item" onClose={onClose} width={520}>
      <ModalHead title={`Move ${line.name}`} sub={`From check ${fromCheck.check_no} · ${money(line.amount_cents)}${shareLabel(line.share) ? ` · ${shareLabel(line.share)} of the item` : ''}`} onClose={onClose} />
      <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[...targets.map((c) => ({ id: c.id, label: `Check ${c.check_no}`, sub: money(c.due_cents) })), { id: null, label: 'A new check', sub: '' }].map((t) => (
          <div key={String(t.id)} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ flex: 1, fontSize: 15, fontWeight: 700 }}>{t.label}<span style={{ color: C.faint, fontWeight: 600 }}>{t.sub ? ` · ${t.sub} due` : ''}</span></span>
            <Btn kind="white" height={48} disabled={busy} onClick={() => move(t.id, false)}>Move all</Btn>
            <Btn kind="white" height={48} disabled={busy} onClick={() => move(t.id, true)}>Move half</Btn>
          </div>
        ))}
        {error && <ErrorNote light error={error} compact />}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ the screen

/** Split payment of ONE check: several cards and cash, one payment at a time (SplitPayFlow). */
function SplitPayModal({ open, check, terminal, settings, qr, onClose, onPaid, notify }) {
  const flow = useRef(null);
  const [paid, setPaid] = useState(false);
  const checkId = check?.id;
  useEffect(() => { if (open) setPaid(false); }, [open, checkId]);
  const terminalRow = terminal?.id;
  const adapter = useMemo(() => {
    if (!checkId) return null;
    const url = `/dining/checks/${checkId}/split`;
    return {
      load: async () => checkView(await api.get(url)),
      next: async (body) => checkView(await api.post(url, body)),
      card: async (n) => {
        const res = await api.post(`${url}/parts/${n}/card`, { terminal_id: terminalRow }, { idempotencyKey: newKey('part') });
        return { view: res.check ? checkView(res.check) : null, payment: res.payment };
      },
      poll: async (paymentId) => {
        const res = await api.get(`/dining/payments/${paymentId}`);
        return { view: res.check ? checkView(res.check) : null, payment: res.payment };
      },
      cash: async (n, tenderedCents) => {
        const res = await api.post(`${url}/parts/${n}/cash`, { tendered_cents: tenderedCents }, { idempotencyKey: newKey('pcash') });
        return { view: checkView(res.check), change: res.change_cents || 0 };
      },
      manual: async (n, method) => checkView((await api.post(`${url}/parts/${n}/manual`, { method }, { idempotencyKey: newKey('pmanual') })).check),
      cancelCard: (paymentId) => api.post(`/dining/payments/${paymentId}/cancel`, {}),
      cancelSplit: async () => checkView(await api.del(url)),
    };
  }, [checkId, terminalRow]);
  if (!open || !adapter) return null;
  const tenders = [
    ...(settings?.card_enabled === false ? [] : [{ id: 'card', label: 'Card' }]),
    ...(settings?.cash_enabled === false ? [] : [{ id: 'cash', label: 'Cash' }]),
    ...(qr?.zelle_qr_url ? [{ id: 'zelle', label: 'Zelle' }] : []),
    ...(qr?.venmo_qr_url ? [{ id: 'venmo', label: 'Venmo' }] : []),
    ...(qr?.crypto_qr_url ? [{ id: 'crypto', label: 'Crypto' }] : []),
  ];
  const close = () => {
    if (paid) { onPaid(); return; }
    if (!flow.current || !flow.current.requestClose()) onClose();
  };
  return (
    <Modal open label="Split payment" onClose={close} width={620} top>
      <ModalHead title="Split payment" sub={`Check ${check.check_no} · several cards and cash, one payment at a time`} onClose={close} />
      <div className="c29-scroll" style={{ padding: '16px 24px 20px', overflowY: 'auto' }}>
        <SplitPayFlow ref={flow} adapter={adapter} tenders={tenders}
          tips={{ enabled: !!settings?.tips_enabled, presets: settings?.tip_presets || [], onTerminal: settings?.terminal_tip_mode === 'terminal' }}
          cardReady={!!terminal} cardHint="Choose this register’s card terminal first (tap the terminal pill)" terminalLabel={terminal?.label}
          onPaid={() => setPaid(true)} onClose={onClose}
          onLeave={(v) => { notify(`Check ${check.check_no} keeps ${money(v.paid)} paid · ${money(v.due)} still due`); onClose(); }} />
        {paid && (
          <div data-testid="split-receipt" style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ReceiptActions kind="check" id={check.id} title={`Receipt · check ${check.check_no}`} />
            <Btn kind="ink" full height={52} onClick={onPaid} data-testid="split-done">Done</Btn>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function CheckoutScreen() {
  const { sessionId } = useParams();
  const sid = Number(sessionId);
  const { me, perms } = useDiningSession();
  const navigate = useNavigate();
  useUpdateHold('dining-checkout', true); // no app update while a check is being paid
  const toast = useToast();
  const now = useNow(1000);
  const { run, dialog } = useApproval();
  const [terminalId, setTerminalId] = useState(readRegister);
  const fetcher = useMemo(() => checkoutPanelFetcher({ token: () => getAuthToken(), terminalId }), [terminalId]);
  const panel = usePaymentStatusPanel(fetcher);
  const live = useLive(async () => {
    const [session, checks] = await Promise.all([api.get(`/dining/sessions/${sid}`), api.get(`/dining/sessions/${sid}/checks`)]);
    return { session, checks };
  }, [sid], { match: (e) => e.payload?.session_id === sid || e.type.startsWith('payment.') || e.type.startsWith('check') });
  const settingsLive = useLive(() => api.get('/checkout/settings'), [], { match: () => false });
  const [payQr, setPayQr] = useState(null); // Zelle / Venmo / Crypto the owner set up (split payment tenders)
  useEffect(() => {
    if (!me?.restaurant_id) return;
    api.get(`/display/payment-qr/${me.restaurant_id}`).then((d) => setPayQr(d?.config || {})).catch(() => setPayQr({}));
  }, [me?.restaurant_id]);
  const [selected, setSelected] = useState(null);
  const [payment, setPayment] = useState(null); // {payment, check} while waiting / the last verdict
  const [tendered, setTendered] = useState('');
  const [tipCents, setTipCents] = useState(0);
  const [busy, setBusy] = useState(null);
  const [modal, setModal] = useState(null);
  const [splitError, setSplitError] = useState(null);
  const [moveLine, setMoveLine] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const cardKey = useRef(null);
  const pollTimer = useRef(null);
  const waitStarted = useRef(null);

  const data = live.data;
  const session = data?.session?.session;
  const answer = data?.checks;
  const checks = useMemo(() => (answer?.checks || []).filter((c) => c.status !== 'void'), [answer]);
  const settings = settingsLive.data;
  const sentItems = (data?.session?.items || []).filter((i) => i.status === 'sent');
  const draftItems = data?.session?.draft || [];
  const current = checks.find((c) => c.id === selected) || checks.find((c) => c.status === 'open') || checks[0] || null;
  const openChecks = checks.filter((c) => c.status === 'open');
  const due = current ? current.due_cents : (data?.session?.totals?.due_cents ?? 0);
  const allPaid = checks.length > 0 && checks.every((c) => c.status === 'paid') && !(answer?.unallocated || []).length;
  const orderPaid = !checks.length && answer?.order_payment_status === 'paid';
  const canRelease = (allPaid || orderPaid) && !draftItems.length && perms.can('tables.release');
  const remaining = checks.reduce((n, c) => n + (c.status === 'open' ? (c.due_cents || 0) : 0), 0);
  const terminalSummary = panel.summary;
  const terminal = thisTerminal(panel.panel);
  // a split payment's card waits in its own modal (SplitPayFlow), never on this screen
  const splitCards = new Set(checks.flatMap((c) => (c.split?.parts || []).flatMap((x) => (x.tries || []).map((t) => t.payment_id))));
  const cardInProgress = checks.flatMap((c) => c.card_payments.map((p) => ({ ...p, check: c })))
    .find((p) => !p.is_final && !splitCards.has(p.payment_id));
  const splitOpen = current?.status === 'open' && (current?.split?.parts || []).some((x) => x.covered_cents > 0 || x.open_payment_id);

  useEffect(() => { if (current && current.id !== selected) setSelected(current.id); }, [current, selected]);
  // another terminal chosen for this register: read the panel again for it
  const firstTerminal = useRef(true);
  useEffect(() => {
    if (firstTerminal.current) { firstTerminal.current = false; return; }
    panel.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminalId]);
  useEffect(() => { setTendered(''); setTipCents(0); }, [current?.id]);

  // ---- the card verdict: poll the payment until Aster Gateway's final word
  const poll = useCallback(async (paymentId) => {
    clearTimeout(pollTimer.current);
    try {
      const res = await api.get(`/dining/payments/${paymentId}`);
      setPayment(res);
      if (!res.payment.is_final) pollTimer.current = setTimeout(() => poll(paymentId), POLL_MS);
      else live.reload();
    } catch (e) {
      // a dropped connection never decides a payment: keep asking
      pollTimer.current = setTimeout(() => poll(paymentId), POLL_MS * 2);
    }
  }, [live]);
  useEffect(() => () => clearTimeout(pollTimer.current), []);
  // resume a card sale in progress (reload, another device started it)
  useEffect(() => {
    if (cardInProgress && (!payment || payment.payment.payment_id !== cardInProgress.payment_id)) {
      waitStarted.current = waitStarted.current || Date.now();
      setSelected(cardInProgress.check.id);
      poll(cardInProgress.payment_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardInProgress?.payment_id]);

  const ensureCheck = async () => {
    if (current) return current;
    const res = await api.post(`/dining/sessions/${sid}/checks`, { mode: 'single', expected_version: answer?.session_version }, { idempotencyKey: newKey('split') });
    const first = (res.checks || []).find((c) => c.status === 'open');
    await live.reload();
    if (first) setSelected(first.id);
    return first;
  };

  const payCard = async (amountCents, tipOverride) => {
    if (!terminal) { toast.error('Choose this register’s card terminal first (tap the terminal pill).'); return; }
    setBusy('card');
    try {
      const check = await ensureCheck();
      if (!check) return;
      cardKey.current = cardKey.current || newKey('card');
      const res = await api.post(`/dining/checks/${check.id}/card`, { terminal_id: terminal.id, tip_cents: tipOverride ?? tipCents, amount_cents: amountCents || undefined, expected_version: check.version }, { idempotencyKey: cardKey.current });
      cardKey.current = null;
      waitStarted.current = Date.now();
      setPayment(res);
      if (!res.payment.is_final) poll(res.payment.payment_id); else live.reload();
    } catch (e) {
      if (!e.network) cardKey.current = null;
      toast.error(errorText(e));
      live.reload();
    } finally {
      setBusy(null);
    }
  };

  const cancelCard = async () => {
    if (!payment) return;
    setBusy('cancel');
    try {
      const res = await api.post(`/dining/payments/${payment.payment.payment_id}/cancel`, {});
      setPayment(res);
      if (!res.payment.is_final) poll(res.payment.payment_id);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const payCash = async () => {
    const tenderedCents = Number(tendered || 0) || due + tipCents;
    setBusy('cash');
    try {
      const check = await ensureCheck();
      if (!check) return;
      const partial = tenderedCents < check.due_cents + tipCents;
      const body = partial
        ? { tendered_cents: tenderedCents, tip_cents: 0, amount_cents: tenderedCents, expected_version: check.version }
        : { tendered_cents: tenderedCents, tip_cents: tipCents, expected_version: check.version };
      const res = await api.post(`/dining/checks/${check.id}/cash`, body, { idempotencyKey: newKey('cash') });
      toast.ok(res.change_cents > 0 ? `Cash recorded · change ${money(res.change_cents)}` : partial ? `${money(tenderedCents)} cash recorded · ${money(res.due_cents)} still due` : 'Cash recorded');
      setTendered('');
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
      live.reload();
    }
  };

  const payOther = async (body) => {
    const check = await ensureCheck();
    await api.post(`/dining/checks/${check.id}/manual`, { ...body, tip_cents: 0, expected_version: check.version }, { idempotencyKey: newKey('manual') });
    toast.ok(`${METHOD_LABEL[body.method] || 'Payment'} recorded`);
    live.reload();
  };

  const split = async (body) => {
    setBusy('split');
    setSplitError(null);
    try {
      await api.post(`/dining/sessions/${sid}/checks`, { ...body, expected_version: answer?.session_version }, { idempotencyKey: newKey('split') });
      setModal(null);
      toast.ok('Checks created');
      setSelected(null);
    } catch (e) {
      setSplitError(errorText(e));
    } finally {
      setBusy(null);
      live.reload();
    }
  };

  const adjust = async (body) => {
    const check = await ensureCheck();
    const res = await run((token) => api.post(`/dining/checks/${check.id}/adjustments`, { ...body, approval_token: token, expected_version: check.version }),
      { action: 'discounts.apply', entityId: check.id, sessionId: sid, what: `Check ${check.check_no}`, reason: body.reason });
    live.reload();
    if (res === null) throw new Error('Approval cancelled - nothing was applied.');
    toast.ok('Applied');
  };

  const removeAdjustment = async (adj) => {
    try {
      await run((token) => api.post(`/dining/checks/${current.id}/adjustments/${adj.id}/remove`, { approval_token: token, expected_version: current.version }),
        { action: 'discounts.apply', entityId: current.id, sessionId: sid, what: `Check ${current.check_no}` });
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      live.reload();
    }
  };

  const move = async (body) => {
    await api.post(`/dining/sessions/${sid}/checks/move`, { ...body, expected_version: answer?.session_version }, { idempotencyKey: newKey('move') });
    toast.ok('Item moved');
    live.reload();
  };

  const voidCheck = async () => {
    if (!current) return;
    setBusy('void');
    try {
      await api.post(`/dining/checks/${current.id}/void`, { reason: 'Re-split at checkout', expected_version: current.version });
      setSelected(null);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(null);
      live.reload();
    }
  };

  const release = async () => {
    setBusy('release');
    try {
      await api.post(`/dining/sessions/${sid}/release`, { expected_version: session?.version });
      toast.ok(`Table ${session?.table_number} released`);
      navigate('/dining/checkout');
    } catch (e) {
      toast.error(errorText(e));
      live.reload();
    } finally {
      setBusy(null);
    }
  };

  const chooseTerminal = (t) => {
    try { localStorage.setItem(REGISTER_KEY, String(t.id)); } catch (e) { /* ignore */ }
    setTerminalId(t.id);
  };

  if (live.error && !data) {
    return (
      <div className="c29 c29-light" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <LightHeader me={me} title="Checkout" sub="Front desk" />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FullError light error={live.error.status === 404 ? 'This table is not open.' : live.error} onRetry={live.error.status === 404 ? () => navigate('/dining/checkout') : live.reload} />
        </div>
      </div>
    );
  }

  const p = payment?.payment;
  const waiting = p && !p.is_final;
  const verdict = p && p.is_final ? p.status : null;
  const showVerdict = verdict && payment?.check?.id === current?.id;
  const stage = waiting ? 'waiting' : showVerdict && verdict === 'approved' ? 'approved' : showVerdict ? 'failed' : 'open';
  const checkIndex = current ? checks.findIndex((c) => c.id === current.id) : 0;
  const lines = current ? current.lines : sentItems.map((i) => ({ order_item_id: i.id, name: i.name, quantity: i.quantity, share: '1/1', amount_cents: i.total_cents, voided: false, seat: i.seat_number, mods: modifierText(i) }));
  const itemOf = Object.fromEntries((data?.session?.items || []).map((i) => [i.id, i]));
  const totalsSrc = current || data?.session?.totals;
  const presets = settings?.tips_enabled && settings?.terminal_tip_mode !== 'terminal' ? (settings.tip_presets || []) : [];
  const tipBase = current ? (current.subtotal_cents - (current.discount_cents || 0) - (current.comp_cents || 0)) : (data?.session?.totals?.subtotal_cents || 0);
  const tipOnTerminal = settings?.tips_enabled && settings?.terminal_tip_mode === 'terminal';
  const cashDisabled = settings && settings.cash_enabled === false;
  const tenderedCents = tendered ? Number(tendered) : null;
  const quick = [{ label: 'Exact', cents: due + tipCents }, ...[500, 1000, 2000].map((step) => Math.ceil((due + tipCents + 1) / step) * step)
    .filter((v, i, a) => v > due + tipCents && a.indexOf(v) === i).slice(0, 3).map((v) => ({ label: `$${(v / 100).toFixed(0)}`, cents: v }))];
  const locked = !!waiting;
  const canRefund = perms.can('payments.refund') || perms.canWithApproval('payments.refund');
  const hasPayments = (c) => c.card_payments.some((cp) => cp.status === 'approved') || c.tenders.length > 0;
  const termLabel = terminal?.label || 'card terminal';

  return (
    <div className="c29 c29-light" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      <LightHeader me={me} title="Checkout" sub={`Front desk${terminal ? ` · ${terminal.label}` : ''}`}
        chip={session ? `Table ${session.table_number} · ${plural(session.party_size, 'guest')}${session.server_name ? ` · Server ${shortName(session.server_name)}` : ''}` : null}
        right={(
          <>
            <button type="button" onClick={() => navigate('/dining/checkout')} className="c29-press" style={{ height: 44, padding: '0 14px', borderRadius: 12, border: `1px solid ${C.border}`, background: '#FFFFFF', color: C.ink2, fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}><IconArrowLeft size={16} />Tables</button>
            <LiveIndicator status={live.streamStatus} light />
            <PaymentStatusPanel status={panel} theme="light" canManage={['owner', 'manager'].includes(me?.role)} onChooseTerminal={chooseTerminal} />
          </>
        )} />
      <StreamBanner status={live.streamStatus} light onRetry={live.reload} />

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 16, padding: 16 }}>
        {/* the check */}
        <section style={{ width: 440, flex: 'none', background: '#FFFFFF', borderRadius: 20, border: '1px solid #ECE6DF', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="c29-scroll" style={{ padding: '14px 18px 10px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${C.hair}`, overflowY: 'hidden' }}>
            <h2 style={{ margin: 0, fontFamily: FONT_TIGHT, fontSize: 22, fontWeight: 800, flex: 1, whiteSpace: 'nowrap' }}>{session ? `Table ${session.table_number}` : ''}</h2>
            {!checks.length && <span style={{ height: 32, padding: '0 10px', borderRadius: 10, background: C.sand, color: C.ink3, fontSize: 12.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>All items · not split</span>}
            {checks.map((c) => {
              const isCur = c.id === current?.id;
              const paid = c.status === 'paid';
              return (
                <button key={c.id} type="button" onClick={() => !locked && setSelected(c.id)} disabled={locked && !isCur}
                  style={{ height: 32, padding: '0 10px', borderRadius: 10, border: paid || isCur ? '1px solid transparent' : `1px solid ${C.border}`, background: paid ? C.greenSoft : isCur ? C.ink : '#FFFFFF', color: paid ? C.greenInk : isCur ? '#FFFFFF' : C.ink3, fontSize: 12.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flex: 'none' }}>
                  Check {c.check_no} · {money(paid ? c.total_cents : c.due_cents)}{paid ? ' · Paid' : ''}
                </button>
              );
            })}
          </div>
          <div className="c29-scroll" style={{ flex: 1, minHeight: 0, padding: '4px 18px' }}>
            {live.loading && !data && <><Skeleton height={36} style={{ margin: '10px 0' }} /><Skeleton height={36} /></>}
            {lines.map((l) => {
              const item = itemOf[l.order_item_id];
              const canMove = current && current.status === 'open' && checks.length > 0 && !locked && perms.can('checks.split') && !(current.card_payments.length || current.tenders.length);
              return (
                <button key={`${l.order_item_id}-${l.share}`} type="button" disabled={!canMove} onClick={() => setMoveLine(l)}
                  style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderBottom: `1px solid ${C.hair2}`, border: 'none', borderBottomStyle: 'solid', borderBottomWidth: 1, borderBottomColor: C.hair2, background: 'transparent', textAlign: 'left', color: C.ink, cursor: canMove ? 'pointer' : 'default', minHeight: 44 }}>
                  <span style={{ minWidth: 28, height: 22, flex: 'none', borderRadius: 6, background: C.sand, color: C.ink3, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item?.seat_number ? `S${item.seat_number}` : 'All'}</span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, textDecoration: l.voided ? 'line-through' : 'none', color: l.voided ? C.faint : C.ink }}>{l.quantity > 1 ? `${l.quantity}× ` : ''}{l.name}{shareLabel(l.share) ? <span style={{ color: C.blueInk, fontWeight: 700 }}> · {shareLabel(l.share)}</span> : null}</span>
                    {item && modifierText(item) && <span style={{ fontSize: 12, color: C.faint }}>{modifierText(item)}</span>}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: l.voided ? C.faint : C.ink }}>{money(l.amount_cents)}</span>
                </button>
              );
            })}
            {current && current.adjustments.map((a) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.hair2}` }}>
                <span style={{ height: 22, padding: '0 8px', borderRadius: 6, background: C.violetSoft, color: C.violet, fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}><IconShield size={12} />{a.kind === 'service_charge' ? 'Service charge' : a.kind === 'comp' ? 'Comp' : 'Discount'}{a.percent ? ` ${Number(a.percent)}%` : ''}</span>
                <span style={{ flex: 1, fontSize: 13, color: C.ink3 }}>{a.reason || ''}{a.approved_by && a.approved_by !== a.applied_by ? ' · Manager approved' : ''}</span>
                {current.status === 'open' && !locked && <button type="button" onClick={() => removeAdjustment(a)} aria-label="Remove" style={{ border: 'none', background: 'transparent', color: C.faint, minHeight: 36, minWidth: 36 }}><IconClose size={16} /></button>}
              </div>
            ))}
            {current && [...current.card_payments.filter((cp) => cp.status === 'approved'), ...current.tenders].map((t) => (
              <div key={t.payment_id ? `p${t.payment_id}` : `t${t.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.hair2}`, fontSize: 13.5 }}>
                <Chip bg={t.voided ? C.redSoft : C.greenSoft} fg={t.voided ? C.redInk : C.greenInk} height={22}>{t.payment_id ? `${t.card_brand || 'Card'} •••• ${t.last4 || ''}` : (METHOD_LABEL[t.method] || 'Tender')}{t.voided ? ' · voided' : ''}</Chip>
                <span style={{ flex: 1, color: C.muted }}>{clock(t.completed_at || t.created_at)}{t.tip_cents > 0 ? ` · tip ${money(t.tip_cents)}` : ''}</span>
                <span style={{ fontWeight: 700 }}>{money(t.payment_id ? t.approved_cents : t.applied_cents)}</span>
              </div>
            ))}
            {(answer?.unallocated || []).length > 0 && checks.length > 0 && (
              <div style={{ margin: '10px 0', borderRadius: 12, background: C.orangeTint, border: '1.5px dashed #FDBA74', padding: '10px 12px', fontSize: 13, color: '#9A3412', lineHeight: 1.45 }}>
                Not on any check yet: {answer.unallocated.map((u) => `${u.name}${shareLabel(u.share) ? ` (${shareLabel(u.share)})` : ''}`).join(', ')}. Re-split or move them onto a check before the table can be released.
              </div>
            )}
            {draftItems.length > 0 && <ErrorNote light compact error={`${plural(draftItems.length, 'item')} on the order pad not sent yet - send or remove them before checkout.`} style={{ margin: '10px 0' }} />}
            {data && !lines.length && <Empty light title="Nothing to pay yet" body="Items sent to the kitchen show here." style={{ padding: '32px 8px' }} />}
          </div>
          <div style={{ padding: '10px 18px 14px', background: C.cream, borderTop: `1px solid ${C.hair}`, display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13.5, color: C.ink3 }}>
            <TotalsRows src={totalsSrc} />
          </div>
        </section>

        {/* center */}
        <section style={{ width: 470, flex: 'none', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
          {stage === 'open' && (
            <>
              <div style={{ background: C.ink, color: '#FFFFFF', borderRadius: 20, padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#FDBA74' }}>
                  {checks.length > 1 ? `Check ${checkIndex + 1} of ${checks.length} · ` : ''}{current?.status === 'paid' ? 'Paid' : 'Balance due'}
                </span>
                <span className="c29-tight" style={{ fontSize: 64, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1.05 }}>{money(current?.status === 'paid' ? 0 : due)}</span>
                <span style={{ fontSize: 13.5, color: '#D6D3D1' }}>
                  {checks.length > 1 ? `Table total ${money(checks.reduce((n, c) => n + (c.total_cents || 0), 0))} · ${money(remaining)} remaining` : (current && current.paid_cents > 0 ? `${money(current.paid_cents)} paid so far` : `Table ${session?.table_number ?? ''} · ${checks.length ? '1 check' : 'not split'} · nothing paid yet`)}
                </span>
              </div>
              {presets.length > 0 && current?.status !== 'paid' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  {[0, ...presets].map((pct) => {
                    const cents = Math.round(tipBase * pct / 100);
                    const on = tipCents === cents;
                    return (
                      <button key={pct} type="button" aria-pressed={on} onClick={() => setTipCents(cents)} className="c29-press"
                        style={{ flex: 1, height: 48, borderRadius: 12, border: on ? `2px solid ${C.orange}` : `1px solid ${C.border}`, background: on ? C.orangeTint : '#FFFFFF', color: on ? '#9A3412' : C.ink, fontSize: 14, fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.15 }}>
                        {pct ? `${pct}%` : 'No tip'}{pct ? <span style={{ fontSize: 11.5, fontWeight: 600, color: C.muted }}>{money(cents)}</span> : null}
                      </button>
                    );
                  })}
                </div>
              )}
              {current?.status === 'paid' ? (
                <div style={{ flex: 1, background: '#FFFFFF', borderRadius: 20, border: '1px solid #ECE6DF', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center' }}>
                  <div style={{ width: 72, height: 72, borderRadius: '50%', background: C.green, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 0 10px ${C.greenSoft}` }}><IconCheck size={36} /></div>
                  <span style={{ fontSize: 18, fontWeight: 800, marginTop: 10 }}>Check {current.check_no} is paid</span>
                  <span style={{ fontSize: 14, color: C.muted, lineHeight: 1.5, maxWidth: 360 }}>
                    {allPaid ? 'Every check on this table is paid. Release the table when the guests leave.' : `${money(remaining)} is still open on the other checks.`}
                  </span>
                  {!allPaid && openChecks[0] && <Btn kind="ink" height={52} onClick={() => setSelected(openChecks[0].id)} style={{ marginTop: 6 }}>Go to check {openChecks[0].check_no}</Btn>}
                  <div style={{ width: '100%', maxWidth: 400, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.hair}`, textAlign: 'left' }}>
                    <ReceiptActions kind="check" id={current.id} title={`Receipt · check ${current.check_no}`} />
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ height: 60, borderRadius: 16, background: '#FFFFFF', border: `2px solid ${C.orange}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px', flex: 'none' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.muted }}>Cash tendered{tipCents ? ` · incl. ${money(tipCents)} tip` : ''}</span>
                    <span className="c29-tight" style={{ fontSize: 28, fontWeight: 800 }}>{money(tenderedCents ?? due + tipCents)}</span>
                  </div>
                  <Keypad value={tendered} onChange={setTendered} quick={quick} />
                </>
              )}
            </>
          )}

          {stage === 'waiting' && (
            <div style={{ flex: 1, background: '#FFFFFF', borderRadius: 20, border: '1px solid #ECE6DF', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <div style={{ position: 'relative', width: 170, height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 6, flex: 'none' }}>
                <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid #FDBA74', animation: 'c29ring 2s ease-out infinite' }} />
                <span style={{ position: 'absolute', inset: 18, borderRadius: '50%', border: '2px solid #FED7AA', animation: 'c29ring 2s ease-out .6s infinite' }} />
                <div style={{ width: 86, height: 124, borderRadius: 16, background: C.ink, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12, gap: 10, boxShadow: '0 16px 30px rgba(28,25,23,.25)' }}>
                  <div className="c29-tight" style={{ width: 62, height: 40, borderRadius: 6, background: C.orange, color: C.orangeDeep, fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{money(p.total_cents)}</div>
                  <span style={{ color: '#FDBA74' }}><IconContactless size={30} /></span>
                </div>
              </div>
              <span style={{ fontSize: 20, fontWeight: 800, textAlign: 'center' }}>{p.status === 'cancelling' ? 'Cancelling on the terminal…' : `Tap, insert or swipe on ${termLabel}`}</span>
              <span className="c29-tight" style={{ fontSize: 56, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1 }}>{money(p.total_cents)}</span>
              <span style={{ fontSize: 14, color: C.muted, textAlign: 'center' }}>
                {checks.length > 1 ? `Check ${checkIndex + 1} of ${checks.length}` : 'Whole check'}{terminal?.terminal_id ? ` · Terminal ID ${terminal.terminal_id}` : ''}{tipOnTerminal ? ' · tip chosen on terminal' : p.tip_cents ? ` · incl. ${money(p.tip_cents)} tip` : ''}
              </span>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                <Step state={p.status === 'starting' ? 'active' : 'done'} label={`Sent to ${terminal?.terminal_id ? `Terminal ID ${terminal.terminal_id}` : termLabel}`} time={waitStarted.current ? clockSeconds(new Date(waitStarted.current).toISOString()) : null} />
                <Step state={p.status === 'starting' ? 'todo' : 'active'} label={p.status === 'cancelling' ? 'Waiting for the terminal to confirm the cancel' : 'Waiting for the guest’s card'} time={waitStarted.current ? elapsed((now - waitStarted.current) / 1000) : null} />
                <Step state="todo" label="Aster Gateway confirms the result" />
              </div>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 12.5, lineHeight: 1.5, color: C.muted, textAlign: 'center' }}>If the terminal stops answering, chain29 asks Aster Gateway for the result before anyone can try again. A request is never counted as a payment.</span>
            </div>
          )}

          {stage === 'approved' && (
            <div style={{ flex: 1, background: '#FFFFFF', borderRadius: 20, border: '1px solid #ECE6DF', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 84, height: 84, borderRadius: '50%', background: C.green, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 0 12px ${C.greenSoft}`, marginTop: 10, flex: 'none' }}><IconCheck size={44} /></div>
              <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.greenInk, marginTop: 10 }}>Approved</span>
              <span className="c29-tight" style={{ fontSize: 56, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1 }}>{money((p.approved_cents || 0) + (p.tip_cents || 0))}</span>
              <span style={{ fontSize: 14, color: C.muted }}>{money(p.approved_cents)} check{p.tip_cents ? ` + ${money(p.tip_cents)} tip` : ''}{checks.length > 1 ? ` · Check ${checkIndex + 1} of ${checks.length}` : ''}</span>
              {p.remaining_cents > 0 && <Chip bg={C.amberSoft} fg={C.amberInk}>{p.message || `${money(p.remaining_cents)} still due on this check`}</Chip>}
              <div style={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 8 }}>
                {[
                  ['Card', [p.card_brand, p.last4 ? `•••• ${p.last4}` : null, p.entry_mode].filter(Boolean).join(' · ') || '—'],
                  ['Auth code', p.auth_code || '—'],
                  ['Terminal', [terminal?.label, terminal?.terminal_id ? `ID ${terminal.terminal_id}` : null].filter(Boolean).join(' · ') || 'Card terminal'],
                  ['Verified by', `Aster Gateway${p.completed_at ? ` · ${clock(p.completed_at)}` : ''}`],
                ].map(([k, v]) => (
                  <div key={k} style={{ background: '#FAF8F5', borderRadius: 12, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.faint }}>{k}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
                  </div>
                ))}
              </div>
              {payment?.check?.status === 'paid' && (
                <div style={{ width: '100%', marginTop: 8 }}><ReceiptActions kind="check" id={payment.check.id} title="Receipt" /></div>
              )}
              <div style={{ flex: 1 }} />
              <Btn kind="white" full height={52} onClick={() => { setPayment(null); const next = openChecks.find((c) => c.id !== current?.id); if (next) setSelected(next.id); }}>
                {openChecks.some((c) => c.id !== current?.id) ? 'Next check' : 'Done'}
              </Btn>
            </div>
          )}

          {stage === 'failed' && (
            <div style={{ flex: 1, background: '#FFFFFF', borderRadius: 20, border: '1px solid #ECE6DF', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
              <div style={{ width: 84, height: 84, borderRadius: '50%', background: verdict === 'declined' ? C.red : C.amber, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 0 12px ${verdict === 'declined' ? C.redSoft : C.amberSoft}`, marginTop: 10, flex: 'none' }}>
                {verdict === 'declined' || verdict === 'cancelled' ? <IconClose size={40} /> : <span className="c29-tight" style={{ fontSize: 40, fontWeight: 800 }}>?</span>}
              </div>
              <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: verdict === 'declined' ? C.redInk : C.amberInk, marginTop: 10 }}>
                {verdict === 'declined' ? 'Declined' : verdict === 'cancelled' ? 'Cancelled' : verdict === 'timed_out' ? 'No answer from the terminal' : 'Payment did not complete'}
              </span>
              <span className="c29-tight" style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-1px' }}>{money(p.total_cents)}</span>
              <span style={{ fontSize: 14, color: C.ink3, lineHeight: 1.5, maxWidth: 380 }}>
                {(p.message && !/^(declined|cancel+ed|timed[ _]?out|error)\.?$/i.test(p.message.trim()) ? `${p.message} · ` : '')}
                {verdict === 'timed_out' || verdict === 'error'
                  ? 'The card may still be approved late - chain29 keeps asking Aster Gateway. Check again before taking another payment.'
                  : 'Nothing was charged. The check keeps its balance - try the card again or take another payment.'}
              </span>
              <div style={{ flex: 1 }} />
              <div style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Btn kind="white" height={56} onClick={() => setPayment(null)}>Other method</Btn>
                <Btn kind="ink" height={56} busy={busy === 'card'} onClick={() => (verdict === 'timed_out' || verdict === 'error' ? payCard(p.amount_cents, p.tip_cents || 0) : payCard())}>
                  {verdict === 'timed_out' || verdict === 'error' ? 'Check again' : 'Try again'}
                </Btn>
              </div>
            </div>
          )}
        </section>

        {/* right */}
        <section className="c29-scroll" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {checks.length > 0 && (checks.some((c) => c.status === 'paid') || allPaid) && (
            <div style={{ borderRadius: 18, background: allPaid ? C.greenSoft : C.violetSoft, color: allPaid ? '#14532D' : '#4C1D95', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4, flex: 'none' }}>
              <span style={{ fontSize: 18, fontWeight: 800 }}>{allPaid ? `All checks paid · Table ${session?.table_number} can be released` : `${checks.filter((c) => c.status === 'paid').length} of ${checks.length} checks paid · ${money(remaining)} remaining`}</span>
              <span style={{ fontSize: 13.5, opacity: 0.85 }}>{allPaid ? 'Every payment is recorded on its check.' : 'Next: any open check - same table, any terminal or cash.'}</span>
            </div>
          )}
          <div style={{ background: '#FFFFFF', borderRadius: 18, border: '1px solid #ECE6DF', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flex: 'none' }}>
            <span style={{ width: 42, height: 42, flex: 'none', borderRadius: 12, background: terminalSummary.tone === 'online' ? '#F0FDF4' : terminalSummary.tone === 'offline' ? '#FEF2F2' : C.sand, color: terminalSummary.tone === 'online' ? C.greenInk : terminalSummary.tone === 'offline' ? C.redInk : C.ink3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconTerminal size={22} /></span>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: terminalSummary.tone === 'online' ? C.greenInk : terminalSummary.tone === 'offline' ? C.redInk : C.ink3, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: terminalSummary.tone === 'online' ? C.greenHi : terminalSummary.tone === 'offline' ? C.redHi : C.faint, flex: 'none' }} />
                {terminalSummary.tone === 'online' ? 'Connected' : terminalSummary.label} · Card terminal{terminal?.terminal_id ? ` · Terminal ID ${terminal.terminal_id}` : ''}
              </span>
              <span style={{ fontSize: 12.5, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{terminal ? `${terminal.label}${terminal.is_this_register ? ' · this register’s default' : ''}` : terminalSummary.reason}</span>
            </div>
            <span style={{ fontSize: 12, color: C.faint, flex: 'none' }}>{panel.checking ? 'Checking…' : ''}</span>
          </div>

          {stage === 'waiting' && (
            <div style={{ flex: 1, background: '#FFFFFF', borderRadius: 18, border: '1px solid #ECE6DF', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.faint }}>Payment in progress</span>
              <span style={{ fontSize: 15, lineHeight: 1.5, color: C.ink2 }}>{tipOnTerminal ? 'The guest picks a tip on the terminal. ' : ''}Other payment methods stay locked until the terminal answers, so this check cannot be paid twice.</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, opacity: 0.45 }}>
                <span style={{ height: 56, borderRadius: 14, background: C.sand, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: C.muted }}>Cash</span>
                <span style={{ height: 56, borderRadius: 14, background: C.sand, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: C.muted }}>Other</span>
              </div>
              <div style={{ flex: 1 }} />
              <Btn kind="dangerOutline" full busy={busy === 'cancel'} disabled={p.status === 'cancelling'} onClick={cancelCard}>{p.status === 'cancelling' ? 'Cancel requested…' : 'Cancel request on terminal'}</Btn>
            </div>
          )}

          {stage !== 'waiting' && current?.status !== 'paid' && (
            <div style={{ background: '#FFFFFF', borderRadius: 18, border: '1px solid #ECE6DF', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, flex: 'none' }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.faint }}>{checks.length > 1 ? `Pay check ${current?.check_no} · ${money(due)}` : 'Pay with'}</span>
              <button type="button" className="c29-press" onClick={() => payCard()} disabled={!perms.can('payments.take') || busy === 'card' || due <= 0 || !!draftItems.length}
                style={{ height: 72, border: 'none', borderRadius: 16, background: C.orange, color: C.orangeDeep, display: 'flex', alignItems: 'center', gap: 14, padding: '0 18px', textAlign: 'left', opacity: due <= 0 || draftItems.length ? 0.5 : 1 }}>
                <IconCard size={28} />
                <span style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 18, fontWeight: 800 }}>{busy === 'card' ? 'Sending…' : 'Card on terminal'}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, opacity: 0.8 }}>{terminal ? `Sends ${money(due + tipCents)} to ${terminal.label}` : 'Choose this register’s terminal first'}</span>
                </span>
                <IconArrowRight size={20} />
              </button>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                <Btn kind="white" height={56} busy={busy === 'cash'} disabled={cashDisabled || due <= 0 || !!draftItems.length} onClick={payCash} style={{ padding: '0 10px' }}>
                  {cashDisabled ? 'Cash off' : tenderedCents !== null && tenderedCents < due + tipCents ? `Cash ${money(tenderedCents)}` : 'Cash'}
                </Btn>
                <Btn kind="white" height={56} disabled={!perms.can('payments.take') || due <= 1 || !!draftItems.length || busy === 'card'} onClick={() => setModal({ kind: 'part' })} style={{ padding: '0 10px' }} data-testid="card-part">Card · part</Btn>
                <Btn kind="white" height={56} disabled={due <= 0 || !!draftItems.length} onClick={() => setModal({ kind: 'other' })} style={{ padding: '0 10px' }}>Other</Btn>
              </div>
              <Btn kind={splitOpen ? 'orange' : 'white'} height={52} disabled={!perms.can('payments.take') || due <= 0 || !!draftItems.length || busy === 'card'}
                onClick={async () => { try { await ensureCheck(); setModal({ kind: 'splitpay' }); } catch (e) { toast.error(errorText(e)); } }}
                icon={<IconSplit size={18} />} data-testid="split-pay-open">
                {splitOpen ? `Split payment · continue · ${money(current?.balance_due_cents ?? due)} left` : 'Split payment · several cards and cash'}
              </Btn>
              {current && current.paid_cents > 0 && <span style={{ fontSize: 12.5, color: C.muted }}>{money(current.paid_cents)} paid so far · {money(due)} left on check {current.check_no}</span>}
            </div>
          )}

          {stage !== 'waiting' && (
            <div style={{ background: '#FFFFFF', borderRadius: 18, border: '1px solid #ECE6DF', padding: 16, display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 200 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.faint }}>Adjust check</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                <Btn kind="white" disabled={!perms.can('checks.split') || !sentItems.length || !!draftItems.length} onClick={() => { setSplitError(null); setModal({ kind: 'split' }); }}>{checks.length ? 'Re-split' : 'Split check'}</Btn>
                <Btn kind="white" disabled={!perms.canWithApproval('discounts.apply') || current?.status === 'paid' || !sentItems.length} onClick={() => setModal({ kind: 'adjust', adj: 'discount' })}>Discount</Btn>
                <Btn kind="white" disabled={!perms.canWithApproval('discounts.apply') || current?.status === 'paid' || !sentItems.length} onClick={() => setModal({ kind: 'adjust', adj: 'service_charge' })}>Service charge</Btn>
                <Btn kind="white" disabled={!perms.canWithApproval('discounts.apply') || current?.status === 'paid' || !sentItems.length} onClick={() => setModal({ kind: 'adjust', adj: 'comp' })}>Comp</Btn>
              </div>
              {current && canRefund && hasPayments(current) && (
                <Btn kind="dangerOutline" height={52} onClick={() => setRefundOpen(true)} data-testid="refund-open">Refund or void a payment on check {current.check_no}</Btn>
              )}
              {current && current.status === 'open' && checks.length > 1 && !current.card_payments.length && !current.tenders.length && !current.adjustments.length && perms.can('checks.split') && (
                <button type="button" onClick={voidCheck} disabled={busy === 'void'} style={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', color: C.redInk, fontSize: 13.5, fontWeight: 700, minHeight: 36, padding: 0 }}>
                  Void check {current.check_no} (its items go back to no check)
                </button>
              )}
              <div style={{ flex: 1 }} />
              <button type="button" className="c29-press" onClick={release} disabled={!canRelease || busy === 'release'}
                style={{ height: 56, borderRadius: 14, border: canRelease ? 'none' : '1px dashed #D6D3D1', background: canRelease ? C.green : '#FFFFFF', color: canRelease ? '#FFFFFF' : C.faint, fontSize: 16, fontWeight: 800 }}>
                {busy === 'release' ? 'Releasing…' : canRelease ? `Close check · release Table ${session?.table_number}` : `Release · ${money(checks.length ? remaining : due)} due`}
              </button>
            </div>
          )}
          {live.error && data && <ErrorNote light compact error={`Showing the last reading - ${errorText(live.error)}`} onRetry={live.reload} />}
        </section>
      </div>

      <SplitModal open={modal?.kind === 'split'} onClose={() => setModal(null)} items={sentItems} partySize={session?.party_size}
        total={data?.session?.totals?.total_cents || 0} onCreate={split} busy={busy === 'split'} error={splitError} />
      <AdjustModal open={modal?.kind === 'adjust'} kind={modal?.adj} onClose={() => setModal(null)} onApply={adjust} subtotal={current?.subtotal_cents ?? data?.session?.totals?.subtotal_cents}
        needsCode={perms.needsCode(modal?.adj === 'service_charge' ? 'checks.service_charge' : 'discounts.apply')} />
      <OtherModal open={modal?.kind === 'other'} onClose={() => setModal(null)} due={due} onPay={payOther} />
      <SplitPayModal open={modal?.kind === 'splitpay' && !!current} check={current} terminal={terminal} settings={settings} qr={payQr}
        notify={(text) => toast.info(text)}
        onClose={() => { setModal(null); live.reload(); }}
        onPaid={() => { setModal(null); live.reload(); }} />
      <PartCardModal open={modal?.kind === 'part'} due={due} total={current?.total_cents ?? due} presets={settings?.tips_enabled ? (settings.tip_presets || []) : []}
        tipOnTerminal={tipOnTerminal} terminalLabel={terminal?.label} onClose={() => setModal(null)} onPay={(amount, tip) => payCard(amount, tip)} />
      <RefundModal open={refundOpen && !!current} check={current} sessionId={sid} run={run} onClose={() => setRefundOpen(false)} onChanged={() => live.reload()} />
      {moveLine && current && <MoveModal line={moveLine} checks={checks} fromCheck={current} onClose={() => setMoveLine(null)} onMove={move} />}
      {dialog}
    </div>
  );
}
